import type { MouseEvent } from 'react';

/** Stop a click inside an interactive cell (checkbox, inline editor, action
 *  button, attachment chip) or the primary-cell `<Link>` from bubbling to the
 *  row's navigation `onClick`.
 *
 *  The finding-row tables (queue list view, audit log, supplier findings)
 *  make the whole `<tr>` a mouse click-target for navigation, while a real
 *  `<Link>` in the row's primary (rule) cell carries the accessible link
 *  semantics — keyboard focus + Enter, cmd/middle-click "open in new tab",
 *  and copy-link. The row itself is deliberately NOT a focus stop / `role`,
 *  so there's no nested link and no bubbled-Enter navigating away from a
 *  focused child control. */
export function stopRowActivation(e: MouseEvent): void {
  e.stopPropagation();
}

/** True when a click landed on (or inside) an interactive control or a
 *  region explicitly opted out of card navigation. Used by control-dense
 *  click-to-open surfaces (the review-queue flashcard) to navigate on a
 *  bare click while leaving every button / input / link / disposition
 *  control working — a `closest()` guard is robust to controls added later,
 *  unlike per-control `stopPropagation`. Mark any non-semantic interactive
 *  region with `data-no-cardnav`. */
export function clickTargetIsInteractive(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return (
    target.closest(
      'button, a, input, textarea, select, label, [role="button"], [role="link"], [data-no-cardnav]',
    ) !== null
  );
}
